/**
 * ErrorBoundary — Reusable error boundary for page sections and routes.
 *
 * Features:
 * - Catches React render errors without crashing the whole app
 * - Auto-retries up to 3 times (page-level) or 1 time (section-level) after 3s delay
 * - Shows styled error card with "Reload" button after max retries exhausted
 * - Logs errors to console (swap for Sentry when ready)
 */

import { Component } from 'react';
import { captureError } from '../lib/sentry';

/** Page-level boundaries retry 3 times; section-level retry once. */
const PAGE_MAX_RETRIES = 3;
const SECTION_MAX_RETRIES = 1;
const RETRY_DELAY_MS = 3000;

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0, autoRetrying: false };
    this._retryTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const label = this.props.label || 'Component';
    console.error(`[ErrorBoundary:${label}] retryCount=${this.state.retryCount}`, error, info?.componentStack);
    captureError(error, { label, retryCount: this.state.retryCount, componentStack: info?.componentStack });

    // Stale chunk after deployment — auto-reload the page once
    if (error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk')) {
      const lastReload = sessionStorage.getItem('juuni_chunk_reload');
      const now = Date.now();
      // Only auto-reload once per 30 seconds to prevent infinite reload loops
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem('juuni_chunk_reload', String(now));
        console.warn('[ErrorBoundary] Stale chunks detected — reloading page');
        window.location.reload();
        return;
      }
    }

    this._scheduleAutoRetry();
  }

  componentWillUnmount() {
    this._clearRetryTimer();
  }

  _getMaxRetries() {
    return this.props.section ? SECTION_MAX_RETRIES : PAGE_MAX_RETRIES;
  }

  _clearRetryTimer() {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  _scheduleAutoRetry() {
    this._clearRetryTimer();

    const maxRetries = this._getMaxRetries();
    if (this.state.retryCount >= maxRetries) return;

    this.setState({ autoRetrying: true });

    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      this.setState((prev) => ({
        hasError: false,
        error: null,
        retryCount: prev.retryCount + 1,
        autoRetrying: false,
      }));
    }, RETRY_DELAY_MS);
  }

  /** Manual retry resets the counter so the user gets fresh auto-retry attempts. */
  handleRetry = () => {
    this._clearRetryTimer();
    this.setState({ hasError: false, error: null, retryCount: 0, autoRetrying: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { section, label } = this.props;
    const { retryCount, autoRetrying } = this.state;
    const maxRetries = this._getMaxRetries();
    const retriesExhausted = retryCount >= maxRetries;
    const title = label ? `${label} Error` : 'Something went wrong';

    // Section-level: compact inline card
    if (section) {
      return (
        <div style={{
          padding: 16, margin: '8px 0', borderRadius: 8,
          background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)',
          fontFamily: 'var(--font-mono, monospace)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-error, #ef4444)', marginBottom: 6 }}>
            {title}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted, #888)', marginBottom: 10 }}>
            {autoRetrying
              ? `Retrying... (attempt ${retryCount + 1} of ${maxRetries})`
              : retriesExhausted
                ? 'This section failed to recover. Other sections are unaffected.'
                : 'This section encountered an error. Other sections are unaffected.'}
          </div>
          {retriesExhausted && (
            <button onClick={this.handleRetry} style={{
              padding: '4px 12px', fontSize: 10, fontFamily: 'var(--font-mono, monospace)',
              background: 'var(--accent-teal)', color: 'var(--bg)', border: 'none', borderRadius: 4, cursor: 'pointer',
            }}>Retry</button>
          )}
        </div>
      );
    }

    // Page-level: centered error card
    return (
      <div style={{
        padding: 40, fontFamily: 'var(--font-mono, monospace)',
        maxWidth: 600, margin: '80px auto', textAlign: 'center',
      }}>
        <div style={{
          fontSize: 48, marginBottom: 16, opacity: 0.6,
        }}>⚠️</div>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-error, #ef4444)', marginBottom: 8 }}>
          {title}
        </h2>
        <p style={{ fontSize: 12, color: 'var(--text-muted, #888)', marginBottom: 20, lineHeight: 1.6 }}>
          {autoRetrying
            ? `Retrying... (attempt ${retryCount + 1} of ${maxRetries})`
            : retriesExhausted
              ? 'Something went wrong. Please refresh.'
              : 'An unexpected error occurred. Your data is safe — this is a display issue.'}
        </p>
        {this.state.error && import.meta.env.DEV && (
          <pre style={{
            whiteSpace: 'pre-wrap', fontSize: 10, textAlign: 'left',
            background: 'rgba(0,0,0,0.05)', color: 'var(--text-error, #ef4444)', padding: 16,
            borderRadius: 8, marginBottom: 16, maxHeight: 200, overflow: 'auto',
          }}>
            {this.state.error.message}{'\n'}{this.state.error.stack}
          </pre>
        )}
        {retriesExhausted ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={this.handleRetry} style={{
              padding: '8px 20px', fontSize: 11, fontFamily: 'var(--font-mono, monospace)',
              background: 'var(--accent-teal)', color: 'var(--bg)', border: 'none', borderRadius: 6,
              cursor: 'pointer', fontWeight: 600,
            }}>Try Again</button>
            <button onClick={() => window.location.reload()} style={{
              padding: '8px 20px', fontSize: 11, fontFamily: 'var(--font-mono, monospace)',
              background: 'transparent', color: 'var(--text-muted, #888)',
              border: '1px solid var(--border, #333)', borderRadius: 6, cursor: 'pointer',
            }}>Reload Page</button>
          </div>
        ) : (
          <div style={{ fontSize: 10, color: 'var(--text-muted, #888)' }}>
            Auto-retry in progress...
          </div>
        )}
      </div>
    );
  }
}
